/**
 * Component to manage terminology system languages
 * Allows creating, editing and deleting languages
 */

import React, { useState, useEffect } from 'react';
import axios from './api/axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

const LanguageManager = ({ token }) => {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    name_native: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, language: null });

  // Load languages on mount and when token changes
  useEffect(() => {
    if (token) {
      loadLanguages();
    }
  }, [token]);

  const loadLanguages = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/terminology/languages/');
      setLanguages(response.data.results || response.data || []);
    } catch (error) {
      showSnackbar('Error loading languages', 'error');
      console.error('Error loading languages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (language = null) => {
    if (language) {
      setEditingLanguage(language);
      setFormData({
        name: language.name,
        name_native: language.name_native
      });
    } else {
      setEditingLanguage(null);
      setFormData({
        name: '',
        name_native: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLanguage(null);
    setFormData({
      name: '',
      name_native: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // Validations
    if (!formData.name || !formData.name_native) {
      showSnackbar('All fields are required', 'warning');
      return;
    }

    // Generate code automatically from English name (first 2 letters)
    const code = editingLanguage ? editingLanguage.code : formData.name.substring(0, 2).toLowerCase();
    
    const dataToSend = {
      ...formData,
      code: code
    };

    try {
      if (editingLanguage) {
        // Update
        await axios.put(
          `/terminology/languages/${editingLanguage.code}/`,
          dataToSend
        );
        showSnackbar('Language updated successfully', 'success');
      } else {
        // Create
        await axios.post(
          '/terminology/languages/',
          dataToSend
        );
        showSnackbar('Language created successfully', 'success');
      }
      handleCloseDialog();
      loadLanguages();
    } catch (error) {
      const message = error.response?.data?.detail || 
                     error.response?.data?.code?.[0] || 
                     'Error saving language';
      showSnackbar(message, 'error');
      console.error('Error saving language:', error);
    }
  };

  const handleDeleteClick = (language) => {
    setDeleteConfirm({ open: true, language });
  };

  const handleDeleteConfirm = async () => {
    const { language } = deleteConfirm;
    try {
      await axios.delete(`terminology/languages/${language.code}/`);
      showSnackbar('Language deleted successfully', 'success');
      loadLanguages();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error deleting language';
      showSnackbar(message, 'error');
      console.error('Error deleting language:', error);
    } finally {
      setDeleteConfirm({ open: false, language: null });
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon />
          <Typography variant="h6">
            Language Management
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Chip 
            label={`Total: ${languages.length} languages`} 
            sx={{ bgcolor: '#f5f5f5' }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Language
          </Button>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ISO Code</strong></TableCell>
              <TableCell><strong>Name (English)</strong></TableCell>
              <TableCell><strong>Native Name</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {languages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No languages registered. Click 'New Language' to add one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              languages.map((language) => (
                <TableRow 
                  key={language.code}
                  sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                >
                  <TableCell>
                    <Chip 
                      label={language.code.toUpperCase()} 
                      size="small"
                      sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>{language.name}</TableCell>
                  <TableCell>{language.name_native}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(language)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(language)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </DialogContent>

      {/* Dialog for Create/Edit */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLanguage ? 'Edit Language' : 'New Language'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Name in English"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              helperText="Example: English, Spanish, German"
              autoFocus
            />
            <TextField
              label="Native Name"
              name="name_native"
              value={formData.name_native}
              onChange={handleInputChange}
              fullWidth
              required
              helperText="Example: English, Español, Deutsch"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              bgcolor: '#000',
              '&:hover': { bgcolor: '#333' }
            }}
          >
            {editingLanguage ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deletion Confirmation Dialog */}
      <Dialog 
        open={deleteConfirm.open} 
        onClose={() => setDeleteConfirm({ open: false, language: null })}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the language <strong>{deleteConfirm.language?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Warning: If there are translations in this language, deletion may fail.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirm({ open: false, language: null })}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LanguageManager;
